import React, { useEffect, useState } from 'react'

import {
  AlignItems,
  Box,
  Display,
  JustifyContent,
  FlexDirection,
  Color,
  BorderStyle,
  Visible,
} from '@gousto-internal/citrus-react'
import { useDispatch, useSelector } from 'react-redux'
import { usePrevious } from 'react-use'

import { trackFailedCheckoutFlow, trackSuccessfulCheckoutFlow } from 'actions/log'
import { redirect } from 'actions/redirect'
import routesConfig from 'config/routes'
import { BoxSummaryDrawer } from 'routes/Checkout/Components/BoxSummaryDrawer/BoxSummaryDrawer'
import { CheckoutStepContainer } from 'routes/Checkout/Components/CheckoutStepContainer/CheckoutStepContainer'
import { CheckoutUrgency } from 'routes/Checkout/Components/CheckoutUrgency/CheckoutUrgency'
import { LoginModal } from 'routes/Checkout/Components/LoginModal'
import {
  clearPayPalClientToken,
  fetchGoustoRef,
  fetchPayPalClientToken,
  firePayPalError,
  trackSignupPageChange,
  trackApplePayEligibility,
} from 'routes/Checkout/checkoutActions'
import {
  Breadcrumbs as BreadcrumbsEnum,
  CHECKOUT_STEPS,
  CheckoutStepIds,
} from 'routes/Checkout/checkoutConfig'
import { useIsApplePayEligible } from 'routes/Checkout/hooks/useIsApplePayEnabled'
import { getIsGoustoOnDemandEnabled } from 'selectors/features'

import { Breadcrumbs } from './Components/Breadcrumbs'
import { OrderSummaryAndBoxDetails } from './Components/OrderSummaryAndBoxDetails'
import { loadCheckoutScript as fetchCheckoutScript } from './loadCheckoutScript'
import { loadPayPalScripts as fetchPayPalScripts } from './loadPayPalScripts'

type CheckoutProps = {
  /**
   * URL params.
   */
  params: {
    /**
     * Step to display in Checkout flow right now.
     */
    stepName?: CheckoutStepIds
  }
}

/**
 * Checkout flow mechanism component.
 * Serves as logic wrapper for all steps in checkout flow.
 * Displays proper checkout step depending on URL.
 */
export const Checkout = ({ params: { stepName: urlStepId } }: CheckoutProps) => {
  const [isCheckoutScriptReady, setIsCheckoutScriptReady] = useState<boolean>(false)
  const [arePaypalScriptsReady, setArePaypalScriptsReady] = useState<boolean>(false)
  const previousUrlStepId = usePrevious(urlStepId)
  const currentStepInfo = CHECKOUT_STEPS.find((checkoutStep) => checkoutStep.id === urlStepId)
  const isGoustoOnDemandEnabled = useSelector(getIsGoustoOnDemandEnabled) as boolean
  const dispatch = useDispatch()
  const isApplePayEligible = useIsApplePayEligible()

  /**
   * Map of Breadcrumb to user-chosen Step.
   * For different breadcrumbs there can be different steps depending on what user clicked, this mapping "freezes" it.
   */
  const [breadcrumbsToStepsMapping, setBreadcrumbsToStepsMapping] = useState<{
    [key in BreadcrumbsEnum]?: CheckoutStepIds
  }>({
    [BreadcrumbsEnum.ACCOUNT]: CheckoutStepIds.ACCOUNT,
  })

  const loadCheckoutScript = () => {
    setIsCheckoutScriptReady(false)

    fetchCheckoutScript()
      .then(() => {
        dispatch(trackSuccessfulCheckoutFlow('Checkout script has been loaded'))
        setIsCheckoutScriptReady(true)
      })
      .catch((error) => {
        dispatch(trackFailedCheckoutFlow('Checkout script has not been loaded', error))
      })
  }

  const loadPayPalScripts = () => {
    if (arePaypalScriptsReady) {
      return
    }

    setArePaypalScriptsReady(false)
    fetchPayPalScripts()
      .then(() => {
        dispatch(trackSuccessfulCheckoutFlow('PayPal scripts have been loaded'))
        setArePaypalScriptsReady(true)
      })
      .catch((error) => {
        dispatch(trackFailedCheckoutFlow('PayPal scripts have not been loaded', error))
        dispatch(firePayPalError(error))
      })

    dispatch(fetchPayPalClientToken())
  }

  const redirectToStep = (stepId?: CheckoutStepIds): void => {
    if (!stepId) {
      return
    }
    const stepInfo = CHECKOUT_STEPS.find((checkoutStep) => checkoutStep.id === stepId)
    if (!stepInfo) {
      // should not happen, but theoretically can
      return
    }
    const nextStepId = stepInfo.id
    dispatch(trackSignupPageChange(nextStepId))
    dispatch(redirect(`${routesConfig.client['check-out']}/${nextStepId}`)) // ID serves as route
  }

  /**
   * First render. Load everything and redirect to "Account" step.
   */
  useEffect(() => {
    // make sure on reload we get to 1st step (Account)
    if (urlStepId !== CheckoutStepIds.ACCOUNT) {
      redirectToStep(CheckoutStepIds.ACCOUNT)
    }

    loadCheckoutScript()
    loadPayPalScripts()

    dispatch(fetchGoustoRef())
    dispatch(trackApplePayEligibility(isApplePayEligible))

    return () => {
      dispatch(clearPayPalClientToken())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Update "breadcrumbs to steps" mapping if stepId has changed.
   */
  useEffect(() => {
    if (!currentStepInfo) return
    setBreadcrumbsToStepsMapping({
      ...breadcrumbsToStepsMapping,
      [currentStepInfo.breadcrumb]: currentStepInfo.id,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlStepId])

  /**
   * Redirect to menu if there's no "stepName" that comes from URL, meaning it's just "/check-out" URL and previous step was "Account".
   * Why:
   * - initially user lands on "/check-out" URL, then user is getting redirected to "Account", meaning there would
   * always be "/check-out" entry in browser history.
   * Happens when:
   * - User is on "Account" step and clicks "Back" browser button, meaning user tries to go back to Menu (if previous
   * history entry is "/menu" from user perspective).
   */
  useEffect(() => {
    if (!urlStepId && previousUrlStepId === CheckoutStepIds.ACCOUNT) {
      dispatch(redirect(routesConfig.client.menu))
    }
  })

  return (
    <Box
      display={Display.Flex}
      justifyContent={JustifyContent.Center}
      paddingH={[0, 8]}
      paddingV={[0, 8, 12]}
      data-testing="checkoutContainer"
      data-testid="checkoutContainer"
    >
      <Box maxWidth="1136px" paddingBottom={[4, 0]} flexGrow={1}>
        <Visible display={[Display.Block, Display.None]}>
          <BoxSummaryDrawer
            isCreatingPreviewOrder={false}
            isGoustoOnDemandEnabled={isGoustoOnDemandEnabled}
          />
        </Visible>
        <CheckoutUrgency />
        <Breadcrumbs
          currentBreadcrumb={currentStepInfo?.breadcrumb}
          breadcrumbsToStepsMapping={breadcrumbsToStepsMapping}
        />
        <Box display={Display.Flex} justifyContent={JustifyContent.Center}>
          <Box
            display={Display.Flex}
            flexDirection={[FlexDirection.Column, null, FlexDirection.Row]}
            alignItems={[null, AlignItems.Center, AlignItems.FlexStart]}
            paddingH={[0, 8]}
            maxWidth={[null, '36rem', 'none']}
          >
            <CheckoutStepContainer
              currentStepId={urlStepId}
              onStepChange={(stepId) => redirectToStep(stepId)}
              isCheckoutScriptReady={isCheckoutScriptReady}
              arePaypalScriptsReady={arePaypalScriptsReady}
              reloadCheckoutScript={loadCheckoutScript}
            />
            <Box
              width={[null, null, '40%']}
              borderColor={Color.ColdGrey_100}
              borderStyle={BorderStyle.Solid}
              borderWidth={0.5}
              data-testing="checkoutDesktopSummary"
            >
              <Visible display={[Display.None, Display.Block]}>
                <OrderSummaryAndBoxDetails
                  isCreatingPreviewOrder={false}
                  isGoustoOnDemandEnabled={isGoustoOnDemandEnabled}
                />
              </Visible>
            </Box>
          </Box>
        </Box>
        <LoginModal />
      </Box>
    </Box>
  )
}
